// src/annihilation/skilltrees/trees.js
// ============================================================
//  ANNIHILATION — the tree aggregator.
//
//  ONE SOURCE OF TRUTH for "which trees does this character
//  have?" SkillTreeView, SkillBar, PlayerController, and any
//  future tooltip/codex all read THIS file — nobody re-derives
//  the lineage-tree-per-being mapping inline. Ever.
//
//  Pure lookup + derivation. All legality still flows through
//  treeEngine (R-UNIVERSAL) — this file only assembles data.
//
//  As the remaining three lineage trees ship (Batches 12–14),
//  each is ONE import + ONE registry line here. Nothing else
//  in the game changes.
// ============================================================

import { TREE_IDS, emptyTreeState, reconcileTreeState } from "./treeEngine";
import SPECTRAL_TIGER_TREE from "./spectralTiger";
import UNIVERSAL_TREE from "./universal";
import TOME_TREE from "./tomeTree";
import AFFINITY_TREE, { nodesForAffinity } from "./affinities";

// ------------------------------------------------------------
// LINEAGE REGISTRY — beingId → lineage tree.
// Chaos Dragon / Z4Z / Xulan land in Batches 12–14 (doc-then-
// code, one being at a time). Until then their entry is null
// and the UI shows the "tree not yet written" panel — never a
// crash, never a blank page.
// ------------------------------------------------------------
export const LINEAGE_TREES = {
  spectral_tiger: SPECTRAL_TIGER_TREE,
  chaos_dragon: null, // Batch 12
  z4z: null, //          Batch 13
  xulan: null, //        Batch 14
};

export const lineageTreeFor = (beingId) => LINEAGE_TREES[beingId] || null;

// ------------------------------------------------------------
// SLOT-UNLOCK NODES — which owned node lights up skill-bar
// slots 5/6/7 for each being. Slots 1–4 are tier-gated, not
// node-gated (beings.js SKILL_SLOTS carries that schedule).
// Internal node ids — vyren pattern, never change.
// ------------------------------------------------------------
export const SLOT_UNLOCK_NODES = {
  spectral_tiger: {
    transformation: "st_g1", // Ghost Form
    kings_grace: "st_k1", //    Nine Lives
    emperium: "st_e1", //       The Ghost Realm
  },
  chaos_dragon: null, // Batch 12
  z4z: null, //          Batch 13
  xulan: null, //        Batch 14
};

// ------------------------------------------------------------
// UNIVERSAL UNLOCK NODES — Shadow / Echo / Mirage, same for
// every being (universal.js is one file, ever).
// ------------------------------------------------------------
export const UNIVERSAL_UNLOCK_NODES = {
  shadow: "uni_s1",
  echo: "uni_ec1",
  mirage: "uni_mr1",
};

// ------------------------------------------------------------
// SAVE-STATE NORMALIZER — Firebase strips empty objects, so a
// fresh character's skillTrees comes back missing `spent`.
// Every consumer normalizes through here before touching the
// engine; the engine then never sees a malformed state.
// ------------------------------------------------------------
export const normalizeTreeState = (raw) => ({
  ...emptyTreeState(),
  ...(raw && typeof raw === "object" ? raw : {}),
  spent: raw && typeof raw.spent === "object" ? raw.spent : {},
});

// ------------------------------------------------------------
// THE FOUR TABS — assembled per character.
// Affinity tab: before commitment it carries Caelarch's full
// eight-way offer (all 120 nodes, for browsing); after
// commitment it narrows to the committed fifteen. treeEngine's
// affinityId check makes off-Affinity spends illegal either
// way — narrowing here is presentation, not law.
// ------------------------------------------------------------
export function treesForCharacter(beingId, treeState) {
  const state = normalizeTreeState(treeState);
  const lineage = lineageTreeFor(beingId);
  const affinityNodes = state.affinityId
    ? nodesForAffinity(state.affinityId)
    : AFFINITY_TREE.nodes;

  return [
    lineage
      ? { key: TREE_IDS.LINEAGE, label: "Lineage", tree: lineage, nodes: lineage.nodes }
      : {
          key: TREE_IDS.LINEAGE,
          label: "Lineage",
          tree: null,
          nodes: [],
          pending: "This lineage's tree has not been written yet.",
        },
    {
      key: TREE_IDS.UNIVERSAL,
      label: "Universal",
      tree: UNIVERSAL_TREE,
      nodes: UNIVERSAL_TREE.nodes,
    },
    {
      key: TREE_IDS.AFFINITY,
      label: "Affinity",
      tree: AFFINITY_TREE,
      nodes: affinityNodes,
    },
    { key: TREE_IDS.TOME, label: "Tome", tree: TOME_TREE, nodes: TOME_TREE.nodes },
  ];
}

/** Every node this character could ever legally interact with. */
export function allNodesForCharacter(beingId, treeState) {
  return treesForCharacter(beingId, treeState).flatMap((t) => t.nodes);
}

/** Owned-node objects (for SkillBar, tooltips, build export). */
export function ownedNodes(beingId, treeState) {
  const state = normalizeTreeState(treeState);
  return allNodesForCharacter(beingId, state).filter((n) => state.spent[n.id]);
}

// ------------------------------------------------------------
//  ⚑⚑ THE CUT-SAFETY LOADER (Power Doctrine · Jul 25 2026) ⚑⚑
//
//  THE CUT deleted sixteen nodes (Spectral Tiger 38→27, Universal
//  20→15+5) and rebuilt the Tome tree's back half. Every one of
//  those ids may be sitting in a living save right now, holding a
//  point hostage: `pointsSpent` would keep counting it forever and
//  `refundNode` could never reach it, because refunding needs a
//  node object that no longer exists.
//
//  Skill points are SCARCE AND PERMANENT — an untouchable design
//  pillar — so a balance decision the player had no part in may
//  NEVER silently eat one. This is the valve.
//
//  Call `loadTreeState` wherever `normalizeTreeState` is called on
//  data coming out of Firebase. It is idempotent and returns the
//  same object identity when nothing changed, so it is safe in a
//  render path.
//
//  ⚑ Reconciles against the UNNARROWED node set on purpose — the
//  Affinity tab narrows to the committed fifteen for presentation,
//  and reconciling against a narrowed list would delete nodes the
//  player legitimately owns.
//
//  ⚑ The Affinity cut is still HELD behind the four-vs-eight
//  ruling (roadmap §4.40). When it lands it needs exactly this
//  same valve and nothing more — that is why this is general.
// ------------------------------------------------------------

/** Every node that exists for this being, regardless of commitment. */
export function allKnownNodes(beingId) {
  const lineage = lineageTreeFor(beingId);
  return [
    ...(lineage ? lineage.nodes : []),
    ...UNIVERSAL_TREE.nodes,
    ...AFFINITY_TREE.nodes, // all of them, never the narrowed fifteen
    ...TOME_TREE.nodes,
  ];
}

/**
 * Normalize + reconcile in one call. Use this on load.
 * @returns {{ state: object, dropped: string[], pointsReturned: number }}
 */
export function loadTreeState(beingId, raw) {
  return reconcileTreeState(normalizeTreeState(raw), allKnownNodes(beingId));
}
